const SCROLL_ALLOW_SELECTOR = '[data-scroll-lock-allow]';

const getElement = (target: EventTarget | null): HTMLElement | null => {
  if (target instanceof HTMLElement) {
    return target;
  }

  if (target instanceof Node) {
    return target.parentElement;
  }

  return null;
};

const canScroll = (element: HTMLElement, deltaY: number): boolean => {
  const maxScrollTop = element.scrollHeight - element.clientHeight;

  if (maxScrollTop <= 1) {
    return false;
  }

  if (deltaY < 0) {
    return element.scrollTop > 0;
  }

  if (deltaY > 0) {
    return element.scrollTop < maxScrollTop - 1;
  }

  return true;
};

const canScrollWithinPage = (target: EventTarget | null, deltaY: number): boolean => {
  let element = getElement(target);

  while (element && element !== document.body && element !== document.documentElement) {
    const style = window.getComputedStyle(element);
    const isScrollContainer =
      element instanceof HTMLTextAreaElement ||
      element.matches(SCROLL_ALLOW_SELECTOR) ||
      style.overflowY === 'auto' ||
      style.overflowY === 'scroll';

    if (isScrollContainer) {
      if (canScroll(element, deltaY)) {
        return true;
      }

      if (style.overscrollBehaviorY === 'contain' || style.overscrollBehaviorY === 'none') {
        return false;
      }
    }

    element = element.parentElement;
  }

  return false;
};

const preventIfNeeded = (event: Event, target: EventTarget | null, deltaY: number): void => {
  if (!document.documentElement.hasAttribute('data-keyboard-open') || deltaY === 0) {
    return;
  }

  if (event.cancelable && !canScrollWithinPage(target, deltaY)) {
    event.preventDefault();
  }
};

export const initIosPageScrollLock = (): (() => void) => {
  let previousTouch: Touch | null = null;

  const handleTouchStart = (event: TouchEvent) => {
    previousTouch = event.touches.length === 1 ? event.touches[0] : null;
  };

  const handleTouchMove = (event: TouchEvent) => {
    const touch = event.touches.length === 1 ? event.touches[0] : null;
    const previous = previousTouch;

    previousTouch = touch;

    if (!touch || !previous || touch.identifier !== previous.identifier) {
      return;
    }

    const deltaY = previous.clientY - touch.clientY;
    const deltaX = previous.clientX - touch.clientX;

    if (Math.abs(deltaY) >= Math.abs(deltaX)) {
      preventIfNeeded(event, event.target, deltaY);
    }
  };

  const handleWheel = (event: WheelEvent) => {
    if (!event.ctrlKey && Math.abs(event.deltaY) >= Math.abs(event.deltaX)) {
      preventIfNeeded(event, event.target, event.deltaY);
    }
  };

  document.addEventListener('touchstart', handleTouchStart, { passive: true });
  document.addEventListener('touchmove', handleTouchMove, { passive: false });
  document.addEventListener('wheel', handleWheel, { passive: false });

  return () => {
    document.removeEventListener('touchstart', handleTouchStart);
    document.removeEventListener('touchmove', handleTouchMove);
    document.removeEventListener('wheel', handleWheel);
  };
};
